import Konva from 'konva';
import { watch } from 'vue';
import HaspObject from './HaspObject';
import { useDatasetStore } from '../stores/datasetStore.js';
import { FontBinding } from '../services/FontBinding.js';
import { useFontStore } from '../stores/fontStore.js';
import { contentBox, resolvePadding, PADDING_SIDE_PROPS } from './properties/v2/padding.js';
import { datasetPlaceholderStatValue } from '../utils/datasetPreview.js';
import { formatObjectDecimals } from './objectValueFormatting.js';

// Keep in sync with legend.js's values_order property comment and the
// firmware's hasp_series_stats_t fields.
const VALID_VALUE_KEYS = ['current', 'min', 'max', 'avg'];

export function formatLegendValue(value, decimals) {
    return formatObjectDecimals(value, decimals);
}

function formatStatValue(ds, i, kind, decimals) {
    return formatLegendValue(datasetPlaceholderStatValue(ds.type, i, kind), decimals);
}

// Real font-metric text measurement, shared by every width calculation below —
// auto-size must never estimate text width (a fixed length*fontSize*factor
// guess drifts from the actual rendered glyphs, especially for proportional
// fonts). Konva.Text itself measures the same way (a canvas 2D context), so
// this stays consistent with what's actually drawn to the layer.
let _measureCtx = null;
function measureTextWidth(text, fontSize, fontFamily = 'Roboto Condensed') {
    if (!_measureCtx) _measureCtx = document.createElement('canvas').getContext('2d');
    _measureCtx.font = `${fontSize}px ${fontFamily}`;
    return Math.ceil(_measureCtx.measureText(text).width);
}

// text_font is really a firmware font-table index (see hasp_font.cpp's
// haspPayloadToFont()), not a literal pixel size, so this can't be a pixel-
// perfect preview of the real device's font. It's used directly as a Konva
// pixel size anyway so changing it gives visible, proportional feedback in
// the canvas — 0 falls back to a size derived from `basis` (the marker size,
// so every row-scoped font stays proportioned to it).
function resolveFontSize(textFont, basis) {
    const explicit = Number(textFont) || 0;
    return explicit > 0 ? explicit : Math.max(10, basis + 4);
}

// Resolves every row-rendering knob once per drawLegend()/computeContentSize()
// call from the four relevant parts' style — shared so drawing and auto-size
// estimation can never disagree about what a row contains (same reasoning as
// the old rowContentWidth()'s doc comment).
function resolveRowLayout(main, marker, label, value, decimals, labelFontFamily, valueFontFamily) {
    const markerSize     = Math.max(2, Number(marker.size) || 12);
    const markerRound    = Number(marker.shape) === 1;
    const markerLabelGap = Math.max(0, Number(marker.spacing) ?? 6);

    const showLabel       = !!main.show_label;
    const labelFontSize   = resolveFontSize(label.text_font, markerSize);
    const labelColor      = label.text_color || '#e2e8f0';
    const labelWidthMode  = label.width_mode === 'fixed' ? 'fixed' : 'auto';
    const labelFixedWidth = Math.max(0, Number(label.fixed_width) || 0);

    const valuesOrder = Array.isArray(main.values_order)
        ? main.values_order.filter(key => VALID_VALUE_KEYS.includes(key))
        : [];
    const valueFontSize = resolveFontSize(value.text_font, markerSize);
    const valueColor    = value.text_color || '#e2e8f0';
    const valueAlign    = value.align === 'center' || value.align === 'right' ? value.align : 'left';

    const showUnit = !!main.show_unit;
    const valueDecimals = Math.max(0, Math.min(6, Number(decimals) || 0));

    const rowHeight = Math.max(markerSize, labelFontSize, valueFontSize) + 4;

    return {
        markerSize, markerRound, markerLabelGap,
        showLabel, labelFontSize, labelFontFamily, labelColor, labelWidthMode, labelFixedWidth,
        valuesOrder, valueFontSize, valueFontFamily, valueColor, valueAlign,
        showUnit, valueDecimals, rowHeight,
    };
}

// Widest formatted value per values_order column across every series in the
// dataset — value columns are aligned like a table, so every row must agree
// on each column's width regardless of that row's own value length.
function computeValueColumnWidths(ds, layout) {
    return layout.valuesOrder.map((key, idx) =>
        Math.max(0, ...ds.series.map((s, i) => measureTextWidth(formatStatValue(ds, i, key, layout.valueDecimals), layout.valueFontSize, layout.valueFontFamily))));
}

// The label column's width — same reasoning as computeValueColumnWidths()
// above, and just as required for table-like alignment: in 'auto' mode this
// must be the widest label across the WHOLE dataset, not each row's own
// label width, or every subsequent value column would drift left/right per
// row depending on that row's label length.
function computeLabelColumnWidth(ds, layout) {
    if (layout.labelWidthMode === 'fixed') return layout.labelFixedWidth;
    return Math.max(0, ...ds.series.map((s, i) => measureTextWidth(s.label || `Series ${i + 1}`, layout.labelFontSize, layout.labelFontFamily)));
}

// One row's content, positioned left-to-right after the marker: label (if
// shown, column-aligned via labelColWidth), then one segment per values_order
// entry (column-aligned via colWidths), then the unit (if shown) trailing on
// its own. Shared by drawLegend() (renders `segments`) and computeContentSize()
// (only needs the returned total `width`) — same reasoning as the module's
// other measure/draw helpers: they must never disagree on what a row contains.
function buildRowSegments(ds, s, i, layout, colWidths, labelColWidth) {
    const { markerSize, markerLabelGap, showLabel, labelFontSize, valuesOrder, valueFontSize, showUnit } = layout;
    const segments = [];
    let x = markerSize + markerLabelGap;

    if (showLabel) {
        const text = s.label || `Series ${i + 1}`;
        segments.push({ kind: 'label', text, x, width: labelColWidth, fontSize: labelFontSize, fontFamily: layout.labelFontFamily });
        x += labelColWidth + 8;
    }

    valuesOrder.forEach((key, idx) => {
        const text  = formatStatValue(ds, i, key, layout.valueDecimals);
        const width = colWidths[idx];
        segments.push({ kind: 'value', text, x, width, fontSize: valueFontSize, fontFamily: layout.valueFontFamily });
        x += width + 8;
    });

    if (showUnit && ds.unit) {
        segments.push({ kind: 'unit', text: ds.unit, x, width: undefined, fontSize: valueFontSize, fontFamily: layout.valueFontFamily });
        x += measureTextWidth(ds.unit, valueFontSize, layout.valueFontFamily);
    }

    return { segments, width: x };
}

export default class HaspLegend extends HaspObject {

    constructor(config) {
        // pad_top/pad_right/pad_bottom/pad_left are folded back generically by
        // ConfigManager's foldPaddingBack (uniform set -> the `padding`
        // shorthand, genuinely unequal sides -> legend's own per-side override
        // properties). A pre-fold here would flatten unequal sides to their max.
        super(config);

        // Text nodes are recreated on every legend draw, so bindings keep the
        // effective family for each independently-selectable text slot current.
        this._labelFontBinding = new FontBinding((family, loaded) => {
            this._labelFontFamily = family;
            if (loaded) this.drawLegend();
        });
        this._valueFontBinding = new FontBinding((family, loaded) => {
            this._valueFontFamily = family;
            if (loaded) this.drawLegend();
        });

        this.on('transform',    () => this.drawLegend());
        this.on('transformend', () => {
            this.size(this.rect.size());
            this.drawLegend();
        });

        // Widget data — stays on the legacy property/update mechanism.
        this.on('dataset', () => this.drawLegend());

        // Style properties — Style API. label_color/marker_size/marker_shape/
        // text_font are superseded by the label/marker/value parts below (see
        // legend.js) but still watched here since they remain declared/settable
        // (backward compat for already-saved designs) — harmless no-op redraws.
        for (const prop of ['columns', 'spacing', 'marker_size', 'marker_shape', 'text_font',
                             'show_label', 'show_unit', 'label_color', 'padding', 'values_order',
                             ...PADDING_SIDE_PROPS]) {
            this.onStyle('main', prop, () => this.drawLegend());
        }
        for (const prop of ['size', 'shape', 'spacing']) {
            this.onStyle('marker', prop, () => this.drawLegend());
        }
        for (const prop of ['text_color', 'text_font', 'width_mode', 'fixed_width']) {
            this.onStyle('label', prop, () => this.drawLegend());
        }
        for (const prop of ['text_color', 'text_font', 'align']) {
            this.onStyle('value', prop, () => this.drawLegend());
        }
        for (const part of ['label', 'value']) {
            this.onStyle(part, 'font', () => this._applyPartFont(part));
            this.onStyle(part, 'text_font_family', () => this._applyPartFont(part));
        }

        // Vertical vs horizontal implies a fundamentally different natural
        // shape (a tall column vs a wide row), so the widget resizes itself
        // to fit its content whenever direction changes — padding included.
        // drawLegend() below also re-resizes when size_kind === 'auto', so this
        // stays correct in both modes.
        this.onStyle('main', 'direction', () => {
            this.resizeToContent();
            this.drawLegend();
        });

        this.draggable(true);
        this._applyPartFont('label');
        this._applyPartFont('value');
        this.drawLegend();

        // Watch the linked dataset in the store — the Legend never caches series
        // data, visibility, or unit itself; any change (series edited, unit/type
        // changed) redraws straight from the live dataset, same pattern as
        // HaspChart.js's dataset watch.
        this._stopDatasetWatch = watch(
            () => {
                if (!this.dataset) return null;
                const ds = useDatasetStore().getDataset(this.dataset);
                if (!ds) return null;
                return JSON.stringify(ds.series) + `:${ds.unit}:${ds.type}:${ds.name}`;
            },
            () => {
                this.drawLegend();
                this.getLayer()?.batchDraw();
            }
        );
        this.on('destroy', () => {
            this._stopDatasetWatch?.();
            this._labelFontBinding.dispose();
            this._valueFontBinding.dispose();
        });
    }

    _partFontResource(part) {
        const font = this.style(part).font;
        return font ? useFontStore().fonts.find(f => f.file === font) : null;
    }

    _applyPartFont(part) {
        const { text_font_family } = this.style(part);
        const binding = part === 'label' ? this._labelFontBinding : this._valueFontBinding;
        binding.set(this._partFontResource(part), text_font_family || 'Roboto Condensed');
    }

    setNewSize(size) {
        // Auto mode derives width/height from content — manual resize (numeric
        // fields, undo/redo of a resize) is a no-op instead of fighting the
        // next drawLegend() call, which would immediately recompute over it.
        if (this.size_kind === 'auto') return;
        super.setNewSize(size);
        this.drawLegend();
    }

    drawLegend() {
        // Single centralized sizing algorithm (computeContentSize) — every
        // layout-affecting property change already ends up calling drawLegend()
        // (see the constructor's prop watchers and the dataset watch below), so
        // hooking the resize in here covers every recalculation trigger at once
        // instead of duplicating this check at each call site.
        if (this.size_kind === 'auto') this.resizeToContent();

        const main   = this.style('main');
        const marker = this.style('marker');
        const label  = this.style('label');
        const value  = this.style('value');

        if (this.legendGroup) this.legendGroup.destroy();
        // Content bounds after the effective per-side padding (see v2/padding.js).
        const box = contentBox(main, this.width(), this.height());
        this.legendGroup = new Konva.Group({ x: box.x, y: box.y, listening: false });
        this.add(this.legendGroup);

        // No standalone mode — a Legend has no data of its own. But an entirely
        // blank widget gives the user no clue it needs a Dataset, so render a
        // placeholder hint instead of nothing (design-time affordance only,
        // never exported, no fabricated series data).
        const ds = this.dataset ? useDatasetStore().getDataset(this.dataset) : null;
        if (!ds || !Array.isArray(ds.series) || ds.series.length === 0) {
            this.drawPlaceholder(!!this.dataset && !!ds, box);
            return;
        }

        const w = Math.max(1, box.width);
        const layout = resolveRowLayout(main, marker, label, value, this.decimals,
            this._labelFontFamily || 'Roboto Condensed', this._valueFontFamily || 'Roboto Condensed');

        const direction  = Number(main.direction) || 0; // 0 = vertical, 1 = horizontal
        const columns    = Math.max(1, Number(main.columns) || 1);
        const spacing    = Math.max(0, Number(main.spacing) ?? 8);
        const colWidthPx   = direction === 0 && columns > 1 ? (w - (columns - 1) * spacing) / columns : w;
        const colWidths    = computeValueColumnWidths(ds, layout);
        const labelColWidth = computeLabelColumnWidth(ds, layout);

        // Horizontal layout needs each item's actual rendered width to flow
        // left-to-right, so it's laid out sequentially while drawing below.
        let cursorX = 0;

        ds.series.forEach((s, i) => {
            let rowX, rowY;
            if (direction === 1) {
                rowX = cursorX;
                rowY = 0;
            } else {
                const col    = i % columns;
                const rowIdx = Math.floor(i / columns);
                rowX = col * (colWidthPx + spacing);
                rowY = rowIdx * (layout.rowHeight + spacing);
            }

            const row = new Konva.Group({ x: rowX, y: rowY, listening: false });

            // Always filled — interactive per-row hiding is firmware-runtime-only,
            // the static preview has no equivalent canvas interaction.
            if (layout.markerRound) {
                row.add(new Konva.Circle({
                    x: layout.markerSize / 2, y: layout.rowHeight / 2, radius: layout.markerSize / 2,
                    fill: s.color || '#4da6ff',
                    listening: false,
                }));
            } else {
                row.add(new Konva.Rect({
                    x: 0, y: (layout.rowHeight - layout.markerSize) / 2, width: layout.markerSize, height: layout.markerSize,
                    fill: s.color || '#4da6ff',
                    listening: false,
                }));
            }

            const { segments, width: rowContentW } = buildRowSegments(ds, s, i, layout, colWidths, labelColWidth);
            segments.forEach(seg => {
                row.add(new Konva.Text({
                    x: seg.x, y: (layout.rowHeight - seg.fontSize) / 2,
                    width: seg.width,
                    align: seg.kind === 'value' ? layout.valueAlign : undefined,
                    text: seg.text, fontSize: seg.fontSize,
                    fontFamily: seg.fontFamily,
                    fill: seg.kind === 'label' ? layout.labelColor : layout.valueColor,
                    listening: false,
                }));
            });

            this.legendGroup.add(row);

            if (direction === 1) cursorX += rowContentW + spacing;
        });
    }

    // Natural size for the current dataset/layout properties, padding included.
    // With no dataset content to size to yet (e.g. before a Dataset is linked),
    // width is left unchanged but height still resolves to a single empty row —
    // an auto-sized Legend with zero height would be invisible and impossible
    // to select/configure.
    computeContentSize() {
        const main   = this.style('main');
        const marker = this.style('marker');
        const label  = this.style('label');
        const value  = this.style('value');

        // Natural size adds the effective padding back around the content, so
        // the horizontal/vertical insets are summed per axis (see v2/padding.js).
        const { left, right, top, bottom } = resolvePadding(main);
        const padX   = left + right;
        const padY   = top + bottom;
        const layout = resolveRowLayout(main, marker, label, value, this.decimals,
            this._labelFontFamily || 'Roboto Condensed', this._valueFontFamily || 'Roboto Condensed');

        const ds = this.dataset ? useDatasetStore().getDataset(this.dataset) : null;
        if (!ds || !Array.isArray(ds.series) || ds.series.length === 0) {
            return { width: this.width(), height: Math.max(1, Math.round(padY + layout.rowHeight)) };
        }

        const direction = Number(main.direction) || 0;
        const columns   = Math.max(1, Number(main.columns) || 1);
        const spacing   = Math.max(0, Number(main.spacing) ?? 8);
        const colWidths     = computeValueColumnWidths(ds, layout);
        const labelColWidth = computeLabelColumnWidth(ds, layout);

        const widths = ds.series.map((s, i) => buildRowSegments(ds, s, i, layout, colWidths, labelColWidth).width);
        const n      = ds.series.length;

        if (direction === 1) {
            // Horizontal: one row, items flow left to right separated by spacing.
            const width  = padX + widths.reduce((a, b) => a + b, 0) + spacing * Math.max(0, n - 1);
            const height = padY + layout.rowHeight;
            return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
        }

        // Vertical: `columns` columns, each sized to the widest item.
        const maxItemWidth = Math.max(...widths);
        const rows          = Math.ceil(n / columns);
        const width  = padX + columns * maxItemWidth + spacing * (columns - 1);
        const height = padY + rows * layout.rowHeight + spacing * (rows - 1);
        return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
    }

    // Resize the widget itself (and its background rect) to match computeContentSize().
    resizeToContent() {
        const size = this.computeContentSize();
        if (size.width === this.width() && size.height === this.height()) return;
        this.rect.size(size);
        this.size(size);
    }

    // Design-time-only hint shown when the Legend has no usable Dataset link.
    // Never exported — purely so an empty Legend doesn't look broken/invisible.
    // Text only, deliberately no rect of its own: this.rect (added by the base
    // class, underneath legendGroup) already renders the real bg_color/
    // border_width/border_color/radius, and a placeholder rect on top of it
    // would visually mask those regardless of what they're set to.
    drawPlaceholder(datasetHasNoSeries, box = { width: this.width(), height: this.height() }) {
        const w = Math.max(1, box.width);
        const h = Math.max(1, box.height);

        const fontSize = 12;
        const lines = datasetHasNoSeries
            ? ['Legend', 'Dataset has no series yet']
            : ['Legend', 'Select a Dataset →'];

        lines.forEach((text, i) => {
            this.legendGroup.add(new Konva.Text({
                x: 0, y: h / 2 - fontSize + i * (fontSize + 4), width: w,
                text, fontSize, fill: '#94a3b8', align: 'center', listening: false,
            }));
        });
    }

    objectExport(page, objectData, hassConfig) {
        super.objectExport(page, objectData, hassConfig);
        // dataset is marked internal (never auto-exported by the generic wire-key
        // loop, since it's a uuid the exporter still needs to resolve to a numeric
        // id) — re-add it explicitly, same as HaspChart.js does for its own link.
        if (this.dataset) {
            objectData.dataset = this.dataset;
        }
        // Auto mode: the firmware computes the final size itself (see
        // legend_calculate_size() in hasp_legend.cpp) — w/h were already set
        // unconditionally by the base export above, so drop them here instead
        // of exporting a stale Designer-time size the firmware will override anyway.
        if (this.size_kind === 'auto') {
            delete objectData.w;
            delete objectData.h;
            objectData.size_kind = 'auto';
        }
    }
}
